import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiCheck, FiTrash2, FiChevronLeft, FiChevronRight, FiSearch, FiLock, FiAlertCircle } from 'react-icons/fi';
import styles from '../Lead.module.css';
import { FLUXOS, fluxoPorId, FORMAS_PAGAMENTO, ESTADOS_CIVIS } from '../fluxos';
import { consultarCnpjLead } from '../../../../services/leads';
import useEsc from '../../../../hooks/useEsc';
import ChecklistDocumentos from './ChecklistDocumentos';
import MinutasLead from './MinutasLead';
import { listarPacotesRotina, rotuloSetor } from '../../../../services/rotinasProgramadas';

/* setores que o lead precisa cobrir com processos (menu Rotinas › Processos) */
const SETORES_OBRIGATORIOS = [['folha', 'Folha'], ['fiscal', 'Fiscal'], ['contabil', 'Contábil']];
const ORDEM_SETOR = ['folha', 'fiscal', 'contabil', 'conformidade', ''];

/* Escolha dos processos do cliente. Agrupa por setor; processo sem setor
   aparece no fim (não conta pra obrigatoriedade -- defina o setor dele na
   aba Processos do menu Rotinas). */
function SeletorProcessos({ processos, selecionados, onConfirmar, onFechar }) {
  const [sel, setSel] = useState(() => new Set(selecionados));
  const [busca, setBusca] = useState('');
  useEsc(onFechar);
  const q = busca.trim().toLowerCase();
  const visiveis = processos.filter(p => p.ativo && (!q || p.nome.toLowerCase().includes(q)));
  const grupos = ORDEM_SETOR.map(st => ({ st, lista: visiveis.filter(p => (p.setor || '') === st) })).filter(g => g.lista.length);
  const alternar = (id) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div className={styles.modalOverlay} style={{ zIndex: 1100 }}>
      <div className={`${styles.modal} ${styles.modalMedio}`}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitulo}>Selecionar processos</h2>
            <p className={styles.modalSubtitulo}>O cliente entra em todas as etapas dos processos marcados</p>
          </div>
          <button type="button" className={styles.modalBtnFechar} onClick={onFechar}><FiX size={20} /></button>
        </div>
        <div className={styles.modalBody}>
          <input className={styles.input} placeholder="Buscar processo..." value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
          {grupos.map(g => (
            <div key={g.st || 'sem'} className={styles.procGrupo}>
              <p className={styles.leadSecaoTitulo}>{g.st ? rotuloSetor(g.st) : 'Sem setor definido (não conta como setor)'}</p>
              {g.lista.map(p => (
                <label key={p.id} className={`${styles.procOpcao} ${sel.has(p.id) ? styles.procOpcaoAtiva : ''}`}>
                  <input type="checkbox" checked={sel.has(p.id)} onChange={() => alternar(p.id)} />
                  <span className={styles.procNome}>{p.nome}</span>
                  <span className={styles.procInfo}>{p.item_ids.length} etapa(s)</span>
                </label>
              ))}
            </div>
          ))}
          {!grupos.length && <p className={styles.leadSecaoSub}>Nenhum processo ativo. Cadastre em Rotinas › Processos.</p>}
        </div>
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnCancelar} onClick={onFechar}>Cancelar</button>
          <button type="button" className={styles.btnSalvar} onClick={() => onConfirmar([...sel])}>Confirmar ({sel.size})</button>
        </div>
      </div>
    </div>
  );
}

export const STATUS_OPCOES = ['Aguardando', 'Aprovado', 'Sem Retorno'];

const TIPOS_SERVICO_PADRAO = ['Abertura', 'Alteração', 'Mensalidade', 'Vencimento'];
const ANEXOS_SIMPLES       = ['I', 'II', 'III', 'IV', 'V'];
const TIPOS_ATIVIDADE      = ['Serviço', 'Comércio', 'Indústria'];
const PORTES_EMPRESA       = ['Normal', 'ME', 'EPP', 'MEI'];
const SETORES              = ['Folha', 'Fiscal', 'Contábil'];
const OPCOES_MOVIMENTO     = ['Com Movimento', 'Sem Movimento'];

/* 4 etapas em 2 colunas -- os campos de antes continuam todos aqui, só
   reagrupados (Dados cadastrais + Negociação na 1ª; Parâmetros legais +
   Obrigações na 2ª) */
const ABAS = [
  { id: 'identificacao', label: 'Identificação e Negociação' },
  { id: 'perfil',        label: 'Perfil da Empresa' },
  { id: 'documentos',    label: 'Documentos e Contrato' },
  { id: 'conclusao',     label: 'Conclusão' },
];

function mascaraTelefone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (!d.length) return '';
  if (d.length <= 2)  return `(${d}`;
  if (d.length <= 6)  return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
}

function mascaraCnpj(v) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

function mascaraCpf(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
}

/* ── blocos de layout: seção em cartão + grade de 4 colunas ──
   Todo campo ocupa 1 a 4 colunas (span) e todos os controles têm a mesma
   altura, então as linhas ficam alinhadas entre si. */
const Secao = ({ titulo, sub, children }) => (
  <section className={styles.secao}>
    <header className={styles.secaoTopo}>
      <h3>{titulo}</h3>
      {sub && <span>{sub}</span>}
    </header>
    <div className={styles.grade}>{children}</div>
  </section>
);
const Campo = ({ rotulo, span = 1, obrigatorio, children }) => (
  <div className={`${styles.campoGrade} ${styles[`s${span}`]}`}>
    {rotulo && <label className={styles.label}>{rotulo}{obrigatorio && <em className={styles.obrigatorio}> *</em>}</label>}
    {children}
  </div>
);
const Chips = ({ opcoes, ativo, onClick, rotulo = (x) => x }) => (
  <div className={styles.tiposGrid}>
    {opcoes.map(o => (
      <button key={o} type="button" className={`${styles.tipoBadge} ${ativo(o) ? styles.tipoBadgeAtivo : ''}`} onClick={() => onClick(o)}>
        {ativo(o) && <FiCheck size={12} style={{ marginRight: 5 }} />}
        {rotulo(o)}
      </button>
    ))}
  </div>
);
const Dinheiro = ({ name, value, onChange }) => (
  <div className={styles.valorInputWrap}>
    <span className={styles.valorPrefix}>R$</span>
    <input name={name} className={styles.valorInput} type="number" min="0" step="0.01" placeholder="0,00" value={value} onChange={onChange} />
  </div>
);


function LeadForm({ lead, onSalvar, onSalvarRascunho, onCancelar, salvando = false, erro = '' }) {
  const [abaAtiva, setAbaAtiva] = useState(0);

  /* ── Aba 1: Dados Cadastrais ── */
  const [form, setForm] = useState({
    nomeContato: lead?.nome      ?? '',
    telefone:    lead?.telefone  ?? '',
    email:       lead?.email     ?? '',
    empresa:     lead?.empresa   ?? '',
    cnpj:        lead?.cnpj      ?? '',
    negociacao:    lead?.negociacao    ?? lead?.anotacoes ?? '',
    codigoCliente: lead?.codigoCliente ?? '',
    status:        lead?.status        ?? 'Aguardando',
  });

  /* ── Aba 2: Negociação ── */
  const servicosIniciais = () => {
    if (!lead) return [];
    if (Array.isArray(lead.servicosDetalhes) && lead.servicosDetalhes.length > 0)
      return lead.servicosDetalhes;
    if (Array.isArray(lead.servicosInteresse) && lead.servicosInteresse.length > 0)
      return lead.servicosInteresse.map(tipo => ({ tipo, valor: '' }));
    return [];
  };
  const [tiposDisponiveis, setTiposDisponiveis] = useState(TIPOS_SERVICO_PADRAO);
  const [servicos, setServicos]     = useState(servicosIniciais);
  const [novoTipo, setNovoTipo]     = useState('');
  const [addingTipo, setAddingTipo] = useState(false);

  /* ── Aba 3: Parâmetros Legais ── */
  const [regimeTributario, setRegimeTributario] = useState(lead?.regimeTributario ?? '');
  const [anexosSimples, setAnexosSimples]       = useState(lead?.anexosSimples    ?? []);
  const [tiposAtividade, setTiposAtividade]     = useState(lead?.tiposAtividade   ?? []);
  const [porteEmpresa, setPorteEmpresa]         = useState(lead?.porteEmpresa     ?? '');
  const [setoresMovimento, setSetoresMovimento] = useState(
    lead?.setoresMovimento ?? { Folha: '', Fiscal: '', Contábil: '' }
  );

  /* ── Aba 4: Obrigações ── */
  const [obrigacoes, setObrigacoes]     = useState(lead?.obrigacoes ?? []);

  /* ── Fluxo, negociação estruturada e dados do contrato ── */
  const [tipoFluxo, setTipoFluxo] = useState(lead?.tipoFluxo ?? '');
  const [contrato, setContrato] = useState({
    honorariosMensais:   lead?.honorariosMensais   ?? '',
    valorServicoPontual: lead?.valorServicoPontual ?? '',
    diaVencimento:       lead?.diaVencimento       ?? '',
    dataInicioServicos:  lead?.dataInicioServicos  ?? '',
    formaPagamento:      lead?.formaPagamento      ?? '',
    condicoesEspeciais:  lead?.condicoesEspeciais  ?? '',
    emailContrato:       lead?.emailContrato       ?? '',
    pendenciasObs:       lead?.pendenciasObs       ?? '',
  });
  const [pendenciasLevantadas, setPendenciasLevantadas] = useState(lead?.pendenciasLevantadas ?? null);
  const [endereco, setEndereco] = useState({
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', municipio: '', uf: '', ...(lead?.endereco || {}),
  });
  const [representante, setRepresentante] = useState({
    nome: '', cpf: '', estado_civil: '', profissao: '', endereco: '', ...(lead?.representante || {}),
  });
  const [erroLocal, setErroLocal] = useState('');
  /* processos (menu Rotinas › Processos) que o cliente vai ter */
  const [processosIds, setProcessosIds] = useState(lead?.processosIds ?? []);
  const [processos, setProcessos] = useState([]);
  const [escolhendoProcessos, setEscolhendoProcessos] = useState(false);
  useEffect(() => { listarPacotesRotina().then(r => setProcessos(r.items || [])).catch(() => {}); }, []);
  const processosSel = processos.filter(p => processosIds.includes(p.id));
  const setoresFaltando = SETORES_OBRIGATORIOS.filter(([st]) => !processosSel.some(p => p.setor === st)).map(([, n]) => n);
  const [receita, setReceita] = useState({ buscando: false, msg: '', erro: '' });
  const ehLegado = !!lead && !lead.tipoFluxo;
  const fluxo = fluxoPorId(tipoFluxo);

  useEsc(onCancelar);

  /* ── Handlers gerais ── */
  function handleChange(e) {
    const { name, value } = e.target;
    let v = value;
    if (name === 'telefone') v = mascaraTelefone(value);
    if (name === 'cnpj')     v = mascaraCnpj(value);
    setForm(prev => ({ ...prev, [name]: v }));
  }

  /* ── Handlers serviços ── */
  function toggleServico(tipo) {
    setServicos(prev => {
      const existe = prev.find(s => s.tipo === tipo);
      if (existe) return prev.filter(s => s.tipo !== tipo);
      return [...prev, { tipo, valor: '' }];
    });
  }
  function setValorServico(tipo, valor) {
    setServicos(prev => prev.map(s => s.tipo === tipo ? { ...s, valor } : s));
  }
  function adicionarTipoCustom() {
    const t = novoTipo.trim().toUpperCase();
    if (!t) return;
    if (!tiposDisponiveis.includes(t)) setTiposDisponiveis(prev => [...prev, t]);
    if (!servicos.find(s => s.tipo === t)) setServicos(prev => [...prev, { tipo: t, valor: '' }]);
    setNovoTipo('');
    setAddingTipo(false);
  }

  /* ── Handlers parâmetros ── */
  function toggleAnexo(a) {
    setAnexosSimples(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  }
  function toggleAtividade(t) {
    setTiposAtividade(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }
  function setMovimento(setor, opcao) {
    setSetoresMovimento(prev => ({ ...prev, [setor]: prev[setor] === opcao ? '' : opcao }));
  }

  /* ── Handlers obrigações ── */
  function removerObrigacao(idx) {
    setObrigacoes(prev => prev.filter((_, i) => i !== idx));
  }

  /* ── Handlers fluxo / contrato ── */
  function setCampoContrato(e) {
    const { name, value } = e.target;
    setContrato(prev => ({ ...prev, [name]: value }));
  }
  const setCampoEndereco = (campo, valor) => setEndereco(prev => ({ ...prev, [campo]: valor }));
  const setCampoRepresentante = (campo, valor) => setRepresentante(prev => ({ ...prev, [campo]: valor }));

  /* Receita: preenche razão social e endereço -- só onde ainda está vazio,
     pra não apagar o que foi digitado à mão */
  async function buscarReceita(cnpj = form.cnpj) {
    const digitos = String(cnpj || '').replace(/\D/g, '');
    if (digitos.length !== 14) { setReceita({ buscando: false, msg: '', erro: 'Informe os 14 dígitos do CNPJ.' }); return; }
    setReceita({ buscando: true, msg: '', erro: '' });
    try {
      const d = await consultarCnpjLead(digitos);
      setForm(prev => ({ ...prev, empresa: prev.empresa?.trim() ? prev.empresa : (d.razaoSocial || prev.empresa) }));
      setEndereco(prev => {
        const novo = { ...prev };
        ['cep', 'logradouro', 'numero', 'complemento', 'bairro', 'municipio', 'uf'].forEach(c => {
          if (!String(novo[c] || '').trim() && d[c]) novo[c] = d[c];
        });
        return novo;
      });
      setReceita({ buscando: false, msg: `Receita: ${d.razaoSocial || 'dados encontrados'}${d.situacaoCadastral ? ` (${d.situacaoCadastral})` : ''}. Razão social e endereço preenchidos onde estavam vazios.`, erro: '' });
    } catch (err) {
      setReceita({ buscando: false, msg: '', erro: err.message });
    }
  }
  function handleCnpj(e) {
    const anterior = form.cnpj.replace(/\D/g, '');
    handleChange(e);
    const digitos = e.target.value.replace(/\D/g, '').slice(0, 14);
    /* completou os 14 dígitos agora: busca sozinho */
    if (digitos.length === 14 && anterior.length !== 14) buscarReceita(digitos);
  }

  /* aprovar (fluxo novo) cria o cliente: exige código e processos dos 3 setores */
  function validarAprovacao() {
    if (!tipoFluxo || form.status !== 'Aprovado') return null;
    if (setoresFaltando.length) return { etapa: 1, msg: `Selecione pelo menos um processo de cada setor. Falta: ${setoresFaltando.join(', ')}.` };
    if (!lead?.clienteCriado && !form.codigoCliente.trim()) return { etapa: 3, msg: 'Informe o código do cliente para aprovar.' };
    return null;
  }

  /* fluxo é obrigatório no lead novo; o antigo (Legado) pode seguir sem */
  function validarEtapa(idx) {
    if (idx === 0 && !ehLegado && !tipoFluxo) return 'Escolha o tipo de fluxo para continuar.';
    if (idx === 0 && !form.nomeContato.trim()) return 'Informe o nome do responsável.';
    return '';
  }
  /* anexos e minutas precisam do lead gravado: o lead é salvo sozinho
     ao chegar na etapa Documentos e Contrato (sem fechar a tela) */
  const [salvandoRascunho, setSalvandoRascunho] = useState(false);
  async function irPara(idx) {
    if (idx > abaAtiva) {
      for (let i = abaAtiva; i < idx; i += 1) {
        const msg = validarEtapa(i);
        if (msg) { setErroLocal(msg); setAbaAtiva(i); return; }
      }
    }
    setErroLocal('');
    /* lead novo, ou fluxo trocado: grava antes pra o checklist ser o do fluxo certo */
    if (idx >= 2 && onSalvarRascunho && (!lead?.id || (tipoFluxo && tipoFluxo !== lead.tipoFluxo))) {
      setSalvandoRascunho(true);
      try { await onSalvarRascunho(montarDados()); } catch (e) { setErroLocal(e.message); setSalvandoRascunho(false); return; }
      setSalvandoRascunho(false);
    }
    setAbaAtiva(idx);
  }

  /* ── Submit ── */
  function handleSubmit(e) {
    e.preventDefault();
    const msg = validarEtapa(0);
    if (msg) { setErroLocal(msg); setAbaAtiva(0); return; }
    const apr = validarAprovacao();
    if (apr) { setErroLocal(apr.msg); setAbaAtiva(apr.etapa); return; }
    setErroLocal('');
    onSalvar(montarDados());
  }

  function montarDados() {
    return ({
      nome:              form.nomeContato,
      empresa:           form.empresa,
      cnpj:              form.cnpj,
      cpf:               '',
      telefone:          form.telefone,
      email:             form.email,
      anotacoes:         form.negociacao,
      negociacao:        form.negociacao,
      status:            form.status,
      servicosInteresse: servicos.map(s => s.tipo),
      servicosDetalhes:  servicos,
      codigoCliente:     form.codigoCliente,
      regimeTributario,
      anexosSimples,
      tiposAtividade,
      porteEmpresa,
      setoresMovimento,
      obrigacoes,
      tipoFluxo,
      ...contrato,
      endereco,
      representante,
      pendenciasLevantadas,
      processosIds,
    });
  }

  const isSel = tipo => servicos.some(s => s.tipo === tipo);

  /* ════════════════════════════════════════
     RENDERIZAÇÃO DAS ABAS
  ════════════════════════════════════════ */

  /* ── Etapa 1: Identificação e Negociação ── */
  function renderIdentificacao() {
    return (
      <>
        <Secao titulo="Identificação" sub="Tipo de fluxo e dados cadastrais atuais">
          <Campo rotulo="Tipo de fluxo" span={2} obrigatorio={!ehLegado}>
            <select className={styles.select} value={tipoFluxo}
              onChange={e => { setTipoFluxo(e.target.value); setErroLocal(''); }}>
              <option value="">{ehLegado ? 'Legado (sem fluxo)' : 'Selecione o tipo de fluxo...'}</option>
              {FLUXOS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </Campo>
          <Campo rotulo={tipoFluxo === 'abertura' ? 'CNPJ (se já houver)' : 'CNPJ'} span={2}>
            <div className={styles.cnpjWrap}>
              <input name="cnpj" className={styles.input} value={form.cnpj}
                onChange={handleCnpj} placeholder="00.000.000/0000-00" maxLength={18} />
              <button type="button" className={styles.btnReceita} onClick={() => buscarReceita()} disabled={receita.buscando}
                title="Buscar razão social e endereço na Receita">
                <FiSearch size={13} /> {receita.buscando ? 'Buscando...' : 'Buscar na Receita'}
              </button>
            </div>
          </Campo>
          {(receita.msg || receita.erro) && (
            <div className={styles.s4}>
              {receita.msg && <p className={styles.avisoReceita}>{receita.msg}</p>}
              {receita.erro && <p className={styles.avisoReceitaErro}>{receita.erro}</p>}
            </div>
          )}
          <Campo rotulo="Razão social / nome da empresa" span={2} obrigatorio>
            <input name="empresa" className={styles.input} value={form.empresa} onChange={handleChange} placeholder="Razão social da empresa" />
          </Campo>
          <Campo rotulo="Nome do responsável" span={2} obrigatorio>
            <input name="nomeContato" className={styles.input} value={form.nomeContato} onChange={handleChange} placeholder="Quem está negociando" />
          </Campo>
          <Campo rotulo="Telefone / WhatsApp">
            <input name="telefone" className={styles.input} value={form.telefone} onChange={handleChange} placeholder="(00) 00000-0000" />
          </Campo>
          <Campo rotulo="E-mail" span={3}>
            <input name="email" type="email" className={styles.input} value={form.email} onChange={handleChange} placeholder="email@empresa.com.br" />
          </Campo>
        </Secao>

        <Secao titulo="Negociação" sub="Serviços de interesse e o que foi combinado">
          <Campo rotulo="Tipo de serviço" span={4}>
            <div className={styles.tiposGrid}>
              {tiposDisponiveis.map(tipo => (
                <button key={tipo} type="button" className={`${styles.tipoBadge} ${isSel(tipo) ? styles.tipoBadgeAtivo : ''}`}
                  onClick={() => toggleServico(tipo)}>
                  {isSel(tipo) && <FiCheck size={12} style={{ marginRight: 5 }} />}
                  {tipo}
                </button>
              ))}
              {addingTipo ? (
                <div className={styles.addTipoRow}>
                  <input className={styles.addTipoInput} value={novoTipo} onChange={e => setNovoTipo(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), adicionarTipoCustom())} placeholder="Nome do tipo..." autoFocus />
                  <button type="button" className={styles.btnAddTipoOk} onClick={adicionarTipoCustom}><FiCheck size={13} /></button>
                  <button type="button" className={styles.btnAddTipoCancel} onClick={() => { setAddingTipo(false); setNovoTipo(''); }}><FiX size={13} /></button>
                </div>
              ) : (
                <button type="button" className={styles.btnNovoTipo} onClick={() => setAddingTipo(true)}>
                  <FiPlus size={13} style={{ marginRight: 4 }} /> Outro tipo
                </button>
              )}
            </div>
          </Campo>
          {servicos.map(s => (
            <Campo key={s.tipo} rotulo={`Valor – ${s.tipo}`}>
              <Dinheiro value={s.valor} onChange={e => setValorServico(s.tipo, e.target.value)} />
            </Campo>
          ))}
          <Campo rotulo="O que foi negociado" span={4}>
            <textarea name="negociacao" className={styles.textarea} value={form.negociacao} onChange={handleChange} rows={4}
              placeholder="Descreva os detalhes da negociação, acordos, condições e valores combinados..." />
          </Campo>
        </Secao>

        <Secao titulo="Condições para o contrato" sub="Entram na minuta do contrato">
          <Campo rotulo="Honorários mensais">
            <Dinheiro name="honorariosMensais" value={contrato.honorariosMensais} onChange={setCampoContrato} />
          </Campo>
          <Campo rotulo={fluxo?.pontual || 'Valor do serviço pontual'}>
            <Dinheiro name="valorServicoPontual" value={contrato.valorServicoPontual} onChange={setCampoContrato} />
          </Campo>
          <Campo rotulo="Dia de vencimento">
            <input name="diaVencimento" className={styles.input} type="number" min="1" max="31" placeholder="Ex.: 10"
              value={contrato.diaVencimento} onChange={setCampoContrato} />
          </Campo>
          <Campo rotulo="Início previsto dos serviços">
            <input name="dataInicioServicos" className={styles.input} type="date" value={contrato.dataInicioServicos} onChange={setCampoContrato} />
          </Campo>
          <Campo rotulo="Forma de pagamento">
            <select name="formaPagamento" className={styles.select} value={contrato.formaPagamento} onChange={setCampoContrato}>
              <option value="">Selecione...</option>
              {[...new Set([...FORMAS_PAGAMENTO, contrato.formaPagamento].filter(Boolean))].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Condições especiais" span={3}>
            <input name="condicoesEspeciais" className={styles.input} value={contrato.condicoesEspeciais} onChange={setCampoContrato}
              placeholder="Ex.: 3 primeiros meses com 50% de desconto" />
          </Campo>
        </Secao>
      </>
    );
  }

  /* ── Etapa 2: Perfil da Empresa ── */
  function renderPerfil() {
    return (
      <>
        <Secao titulo="Perfil tributário">
          <Campo rotulo="Regime tributário" span={4}>
            <Chips opcoes={['Simples Nacional', 'Lucro Presumido', 'Lucro Real']} ativo={r => regimeTributario === r}
              onClick={r => setRegimeTributario(prev => (prev === r ? '' : r))} />
          </Campo>
          {regimeTributario === 'Simples Nacional' && (
            <Campo rotulo="Anexo(s) do Simples — pode marcar mais de um" span={4}>
              <Chips opcoes={ANEXOS_SIMPLES} ativo={a => anexosSimples.includes(a)} onClick={toggleAnexo} rotulo={a => `Anexo ${a}`} />
            </Campo>
          )}
          <Campo rotulo="Tipo de atividade (pode marcar mais de um)" span={2}>
            <Chips opcoes={TIPOS_ATIVIDADE} ativo={t => tiposAtividade.includes(t)} onClick={toggleAtividade} />
          </Campo>
          <Campo rotulo="Porte da empresa" span={2}>
            <Chips opcoes={PORTES_EMPRESA} ativo={p => porteEmpresa === p} onClick={p => setPorteEmpresa(prev => (prev === p ? '' : p))} />
          </Campo>
        </Secao>

        <Secao titulo="Setores de movimento">
          {SETORES.map(setor => (
            <Campo key={setor} rotulo={setor} span={setor === 'Contábil' ? 2 : 1}>
              <div className={styles.setorOpcoes}>
                {OPCOES_MOVIMENTO.map(opcao => (
                  <button key={opcao} type="button"
                    className={`${styles.setorBtn} ${setoresMovimento[setor] === opcao
                      ? (opcao === 'Com Movimento' ? styles.setorBtnAtivoCom : styles.setorBtnAtivoSem) : ''}`}
                    onClick={() => setMovimento(setor, opcao)}>
                    {opcao}
                  </button>
                ))}
              </div>
            </Campo>
          ))}
        </Secao>

        <Secao titulo="Processos" sub="Do menu Rotinas › Processos — definem as obrigações do cliente">
          <div className={styles.s4}>
            <div className={styles.procCobertura}>
              {SETORES_OBRIGATORIOS.map(([st, nome]) => {
                const tem = processosSel.some(p => p.setor === st);
                return (
                  <span key={st} className={`${styles.procSetor} ${tem ? styles.procSetorOk : styles.procSetorFalta}`}>
                    {tem ? <FiCheck size={12} /> : <FiAlertCircle size={12} />} {nome}
                  </span>
                );
              })}
              <button type="button" className={styles.btnAdicionarObrigacao} onClick={() => setEscolhendoProcessos(true)}>
                <FiPlus size={15} /> Selecionar processos
              </button>
            </div>
            <p className={styles.leadSecaoSub} style={{ margin: '8px 0 0' }}>
              Obrigatório pelo menos um processo de Folha, Fiscal e Contábil para aprovar. Ao aprovar, o cliente já entra nas rotinas desses processos (bloqueado até concluir o Onboard).
            </p>
          </div>
          {processosSel.length > 0 && (
            <div className={styles.s4}>
              <div className={styles.procLista}>
                {[...processosSel].sort((x, y) => ORDEM_SETOR.indexOf(x.setor || '') - ORDEM_SETOR.indexOf(y.setor || '')).map(p => (
                  <span key={p.id} className={styles.procChip}>
                    <em>{p.setor ? rotuloSetor(p.setor) : 'sem setor'}</em> {p.nome}
                    <button type="button" title="Tirar" onClick={() => setProcessosIds(ids => ids.filter(x => x !== p.id))}><FiX size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {obrigacoes.length > 0 && (
            <div className={styles.s4}>
              <p className={styles.leadSecaoSub} style={{ margin: '6px 0' }}>Obrigações anotadas antes dos processos (legado):</p>
              <div className={styles.obrigacoesList}>
                {obrigacoes.map((o, i) => (
                  <div key={i} className={styles.obrigacaoItem}>
                    <div className={styles.obrigacaoTextos}>
                      <span className={styles.obrigacaoNome}>{o.nome}</span>
                      {o.descricao && <span className={styles.obrigacaoDesc}>{o.descricao}</span>}
                    </div>
                    <button type="button" className={styles.btnRemoverObrigacao} onClick={() => removerObrigacao(i)}><FiTrash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Secao>
        {escolhendoProcessos && (
          <SeletorProcessos processos={processos} selecionados={processosIds}
            onConfirmar={ids => { setProcessosIds(ids); setEscolhendoProcessos(false); setErroLocal(''); }}
            onFechar={() => setEscolhendoProcessos(false)} />
        )}
      </>
    );
  }

  /* ── Etapa 3: Documentos e Contrato ── */
  function renderDocumentos() {
    const end = (c, rotulo, span = 1, ph = '') => (
      <Campo rotulo={rotulo} span={span}>
        <input className={styles.input} value={endereco[c] || ''} placeholder={ph} maxLength={c === 'uf' ? 2 : undefined}
          onChange={e => setCampoEndereco(c, c === 'uf' ? e.target.value.toUpperCase() : e.target.value)} />
      </Campo>
    );
    return (
      <>
        <Secao titulo="Endereço da empresa" sub="Como vai sair no contrato">
          {end('cep', 'CEP', 1, '00000-000')}
          {end('logradouro', 'Logradouro', 2, 'Rua, avenida...')}
          {end('numero', 'Número')}
          {end('complemento', 'Complemento')}
          {end('bairro', 'Bairro')}
          {end('municipio', 'Município')}
          {end('uf', 'UF', 1, 'SP')}
        </Secao>

        <Secao titulo="Representante legal">
          <Campo rotulo="Nome completo" span={2}>
            <input className={styles.input} value={representante.nome} onChange={e => setCampoRepresentante('nome', e.target.value)} />
          </Campo>
          <Campo rotulo="CPF">
            <input className={styles.input} value={representante.cpf} placeholder="000.000.000-00"
              onChange={e => setCampoRepresentante('cpf', mascaraCpf(e.target.value))} />
          </Campo>
          <Campo rotulo="Estado civil">
            <select className={styles.select} value={representante.estado_civil} onChange={e => setCampoRepresentante('estado_civil', e.target.value)}>
              <option value="">Selecione...</option>
              {ESTADOS_CIVIS.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </Campo>
          <Campo rotulo="Profissão">
            <input className={styles.input} value={representante.profissao} onChange={e => setCampoRepresentante('profissao', e.target.value)} />
          </Campo>
          <Campo rotulo="Endereço do representante" span={3}>
            <input className={styles.input} value={representante.endereco} placeholder="Rua, número, bairro, cidade/UF, CEP"
              onChange={e => setCampoRepresentante('endereco', e.target.value)} />
          </Campo>
        </Secao>

        <Secao titulo="Envio e pendências">
          <Campo rotulo="E-mail para envio do contrato" span={2}>
            <input name="emailContrato" type="email" className={styles.input} value={contrato.emailContrato}
              onChange={setCampoContrato} placeholder={form.email || 'email@empresa.com.br'} />
          </Campo>
          <Campo rotulo="Pendências já levantadas?" span={2}>
            <Chips opcoes={['Sim', 'Não']} ativo={o => pendenciasLevantadas === (o === 'Sim')}
              onClick={o => setPendenciasLevantadas(prev => (prev === (o === 'Sim') ? null : o === 'Sim'))} />
          </Campo>
          <Campo rotulo="Observação das pendências" span={4}>
            <input name="pendenciasObs" className={styles.input} value={contrato.pendenciasObs} onChange={setCampoContrato}
              placeholder="Débitos, certidões, parcelamentos, declarações em atraso..." />
          </Campo>
        </Secao>

        <Secao titulo="Checklist de documentos">
          <div className={styles.s4}>
            {lead?.id
              ? <ChecklistDocumentos key={`${lead.id}|${lead.tipoFluxo}`} leadId={lead.id} fluxo={tipoFluxo || null} />
              : <p className={styles.leadSecaoSub}>Salve o lead para anexar documentos.</p>}
          </div>
        </Secao>

        <Secao titulo="Minutas do contrato">
          <div className={styles.s4}>
            {lead?.id
              ? <MinutasLead leadId={lead.id} antesDeGerar={onSalvarRascunho ? () => onSalvarRascunho(montarDados()) : undefined} />
              : <p className={styles.leadSecaoSub}>Salve o lead para gerar a minuta.</p>}
          </div>
        </Secao>
      </>
    );
  }

  function renderConclusao() {
    return (
      <div className={styles.abaConteudo}>
        <div className={styles.abaBloco}>
          <p className={styles.leadSecaoTitulo}>Status do Lead</p>
          <div className={styles.statusOpcoes}>
            {STATUS_OPCOES.map(s => (
              <button key={s} type="button"
                className={`${styles.statusBtn} ${styles[`statusBtn_${s.replace(/\s/g,'')}`]} ${form.status === s ? styles.statusBtnAtivo : ''}`}
                onClick={() => setForm(prev => ({ ...prev, status: s }))}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {form.status === 'Aprovado' && (
          <div className={styles.abaBloco}>
            <p className={styles.leadSecaoTitulo}>Código do Cliente</p>
            <p className={styles.leadSecaoSub}>
              Defina o código que identificará este cliente no sistema após o onboard.
            </p>
            <input
              name="codigoCliente"
              className={styles.input}
              value={form.codigoCliente}
              onChange={handleChange}
              placeholder="Ex: 001, CLI-2024-001..."
              style={{ maxWidth: 320 }}
              disabled={!!lead?.clienteCriado}
            />
            {lead?.clienteCriado ? (
              <p className={styles.statusInfo}><FiLock size={12} /> Cliente já criado com este código.</p>
            ) : tipoFluxo ? (
              <p className={styles.statusInfo}>
                Ao salvar, o cliente é criado com este código e os dados do lead, entra nos processos selecionados e vai para o <strong>Onboard</strong>.
                No cadastro de clientes e nas rotinas ele fica <strong>bloqueado</strong> até o Onboard ser concluído.
              </p>
            ) : (
              <p className={styles.statusInfo}>
                Este lead será movido para o <strong>Onboard</strong> e o cliente será criado automaticamente ao concluir.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  const renderAbas = [
    renderIdentificacao,
    renderPerfil,
    renderDocumentos,
    renderConclusao,
  ];

  /* ════════════════════════════════════════
     LAYOUT PRINCIPAL
  ════════════════════════════════════════ */

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitulo}>{lead ? 'Editar Lead' : 'Novo Lead'}</h2>
            <p className={styles.modalSubtitulo}>
              {ABAS[abaAtiva].label} — etapa {abaAtiva + 1} de {ABAS.length}
              {fluxo ? ` · ${fluxo.curto}` : ehLegado ? ' · Legado' : ''}
            </p>
          </div>
          <button type="button" className={styles.modalBtnFechar} onClick={onCancelar}>
            <FiX size={20} />
          </button>
        </div>

        {/* ── Tab Nav ── */}
        <div className={styles.tabNav}>
          {ABAS.map((aba, i) => (
            <button key={aba.id} type="button"
              className={`${styles.tabBtn} ${i === abaAtiva ? styles.tabBtnAtivo : ''} ${i < abaAtiva ? styles.tabBtnConcluido : ''}`}
              onClick={() => irPara(i)}>
              <span className={styles.tabNumero}>{i < abaAtiva ? <FiCheck size={11} /> : i + 1}</span>
              <span className={styles.tabLabel}>{aba.label}</span>
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div className={styles.modalBody}>
          {(erroLocal || erro) && <div className={styles.erroModal}>{erroLocal || erro}</div>}
          <form id="leadForm" onSubmit={handleSubmit} className={styles.leadFormWrap}>
            {renderAbas[abaAtiva]()}
          </form>
        </div>

        {/* ── Footer ── */}
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnCancelar} onClick={onCancelar} disabled={salvando}>
            Cancelar
          </button>
          <div className={styles.navBtns}>
            {abaAtiva > 0 && (
              <button type="button" className={styles.btnAnterior} onClick={() => irPara(abaAtiva - 1)}>
                <FiChevronLeft size={16} /> Anterior
              </button>
            )}
            {abaAtiva < ABAS.length - 1 ? (
              <button type="button" className={styles.btnProximo} onClick={() => irPara(abaAtiva + 1)} disabled={salvandoRascunho}>
                {salvandoRascunho ? 'Salvando...' : 'Próximo'} <FiChevronRight size={16} />
              </button>
            ) : (
              <button type="submit" form="leadForm" className={styles.btnSalvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar Lead'}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default LeadForm;
